import * as React from 'react';
import { useState } from 'react';
import {
    cn,
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@ai-content-hub/content-kit';
import { Loader2, RotateCcw, Sparkles, User } from 'lucide-react';
import type { ChatMessageData } from './types';
import { usePromptTranslations } from '../../i18n';

export interface ChatMessageProps {
    message: ChatMessageData;
    onRollback?: (version: number) => void;
    isRollbackLoading?: boolean;
}

function formatTime(iso: string): string {
    try {
        return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
        return '';
    }
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
        return value as Record<string, unknown>;
    }
    return undefined;
}

function formatMetadataLines(metadata: Record<string, unknown>): string[] {
    // WHY: Labels map Agent recipe option keys for chat history display
    // (GEN_QUESTION_CONTRACT_SSOT §3). Keep keys in sync with form wire payloads.
    const labelMap: Record<string, string> = {
        media: 'Media',
        num_blanks: 'Số lượng chỗ trống',
        distractors_per_blank: 'Distractors / blank',
        num_left: 'Left items',
        num_right: 'Right items',
        num_items: 'Số lượng mục',
        num_questions: 'Số câu hỏi',
        num_options: 'Số lựa chọn',
        num_correct_min: 'Đáp án đúng (min)',
        num_correct_max: 'Đáp án đúng (max)',
        source_type: 'Nguồn',
    };

    const option = asRecord(metadata.option);
    const flattened: Record<string, unknown> = { ...metadata };
    if (option) {
        for (const key of Object.keys(labelMap)) {
            if (key === 'source_type') continue;
            if (option[key] !== undefined) {
                flattened[key] = option[key];
            }
        }
    }

    const skip = new Set([
        'is_regeneration',
        'content_id',
        'ai_content_type_id',
        'content_type_id',
        'option',
        'document_urls',
        'document_names',
    ]);
    const lines: string[] = [];

    for (const [key, value] of Object.entries(flattened)) {
        if (skip.has(key) || value === undefined || value === null || value === '') continue;
        const label = labelMap[key];
        if (label) {
            lines.push(`- ${label}: ${String(value)}`);
        }
    }
    return lines;
}

export const ChatMessage: React.FC<ChatMessageProps> = ({
    message,
    onRollback,
    isRollbackLoading,
}) => {
    const isUser = message.role === 'user';
    const isPending = message.status === 'pending';
    const isFailedMessage = message.status === 'failed';
    const time = formatTime(message.timestamp);
    const showRollback = !isUser && message.version && !message.isCurrent && onRollback;
    const [confirmOpen, setConfirmOpen] = useState(false);
    const pt = usePromptTranslations();

    const handleConfirmRollback = () => {
        setConfirmOpen(false);
        onRollback?.(message.version!);
    };

    return (
        <div
            className={cn(
                'chat-message flex gap-2 max-w-[85%]',
                isUser ? 'ml-auto flex-row-reverse' : 'mr-auto'
            )}
        >
            {/* <div
                className={cn(
                    'flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center mt-1',
                    isUser ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground',
                )}
            >
                {isUser ? <User className="h-3.5 w-3.5" /> : <Sparkles className="h-3.5 w-3.5" />}
            </div> */}

            <div className="flex flex-col gap-1">
                <div
                    className={cn(
                        'chat-message-bubble rounded-xl px-3.5 py-2.5 text-sm leading-relaxed',
                        isUser
                            ? 'bg-primary text-primary-foreground rounded-tr-sm'
                            : 'bg-muted text-foreground rounded-tl-sm'
                    )}
                >
                    {isUser && message.metadata ? (
                        <div className="chat-message-metadata whitespace-pre-wrap text-[13px] px-3 py-2 text-center">
                            {message.content && <p className="font-medium">{message.content}</p>}
                            {formatMetadataLines(message.metadata).map((line, i) => (
                                <p key={i} className="opacity-90">
                                    {line}
                                </p>
                            ))}
                        </div>
                    ) : (
                        <p
                            className={cn(
                                'chat-message-metadata whitespace-pre-wrap text-[13px] p-3',
                                isPending && 'flex items-center gap-2 italic text-muted-foreground',
                                isFailedMessage && 'text-destructive'
                            )}
                        >
                            {isPending && (
                                <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
                            )}
                            {message.content}
                        </p>
                    )}
                </div>

                <div
                    className={cn(
                        'flex items-center gap-2 px-1 text-[10px]',
                        isUser ? 'justify-end' : 'justify-start'
                    )}
                >
                    {time && <span className="text-xs text-muted-foreground">{time}</span>}
                    {showRollback && (
                        <>
                            <button
                                type="button"
                                onClick={() => setConfirmOpen(true)}
                                disabled={isRollbackLoading}
                                className={cn(
                                    'chat-message-rollback inline-flex items-center gap-1 text-[11px]',
                                    'text-muted-foreground hover:text-primary cursor-pointer',
                                    'transition-colors duration-150',
                                    isRollbackLoading && 'opacity-50 pointer-events-none'
                                )}
                            >
                                <RotateCcw className="h-3 w-3" />
                            </button>

                            <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                                <AlertDialogContent>
                                    <AlertDialogHeader>
                                        <AlertDialogTitle>
                                            {pt.rollbackConfirmTitle.replace('{version}', String(message.version))}
                                        </AlertDialogTitle>
                                        <AlertDialogDescription>
                                            {pt.rollbackConfirmMessage.replace(/\{version\}/g, String(message.version))}
                                        </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                        <AlertDialogCancel>{pt.cancel}</AlertDialogCancel>
                                        <AlertDialogAction onClick={handleConfirmRollback}>
                                            {pt.rollback}
                                        </AlertDialogAction>
                                    </AlertDialogFooter>
                                </AlertDialogContent>
                            </AlertDialog>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};
